import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
} from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Device } from "types/device.types";
import { theme } from "styles/theme";
import GoogleMaps, { Markers } from "components/GoogleMapBox";
import { InfoWindow } from "@vis.gl/react-google-maps";
import { useAuth } from "context/AuthContext";
import { buildDeviceDetailsPath } from "utils/deviceNavigation";
// import { Button } from "primereact/button";

const MapContainer = styled.div`
  height: 100%;
  width: 100%;
  min-height: 50vh;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
`;

// const SearchByLocationButton = styled.div`
//   position: absolute;
//   top: 10px;
//   left: 50%;
//   transform: translateX(-50%);
//   z-index: 10;
//   opacity: 0;
//   transition: opacity 0.3s ease-in-out;

//   &.visible {
//     opacity: 1;
//   }
// `;

// const LocationSearchActiveIndicator = styled.div`
//   position: absolute;
//   top: 60px;
//   right: 10px;
//   z-index: 10;
//   background-color: rgba(255, 255, 255, 0.9);
//   padding: 5px 10px;
//   border-radius: 4px;
//   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
//   display: flex;
//   align-items: center;
//   gap: 5px;
// `;

const InfoWindowContent = styled.div`
  padding: 5px;
`;

const DeviceName = styled.h4`
  margin: 0 0 5px 0;
  color: ${theme.colors.tertiary};
`;

const DeviceInfo = styled.div`
  margin-bottom: 5px;
  font-size: 12px;
`;

const AlarmBadge = styled.span`
  display: inline-block;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
`;

const ErrorBadge = styled(AlarmBadge)`
  background-color: #ff3b30;
`;

const WarningBadge = styled(AlarmBadge)`
  background-color: #ff9500;
`;

const ViewDetailsLink = styled(Link)`
  display: block;
  margin-top: 10px;
  color: ${theme.colors.tertiary};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

interface DevicesMapProps {
  devices: Device[];
  selectedDevice?: Device | null;
  onDeviceSelect?: (device: Device | null) => void;
  onLocationSearch?: (lat: number, lng: number, radiusKm: number) => void;
  isLocationSearchActive?: boolean;
  onClearLocationSearch?: () => void;
}

const DevicesMap: React.FC<DevicesMapProps> = ({
  devices,
  selectedDevice: externalSelectedDevice,
  onDeviceSelect,
  onLocationSearch,
  isLocationSearchActive,
  onClearLocationSearch,
}) => {
  const [internalSelectedDevice, setInternalSelectedDevice] =
    useState<Device | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  // Combine external and internal selected device state
  const selectedDevice = externalSelectedDevice || internalSelectedDevice;
  // Define a function to center the map
  const centerMapOnDevice = useCallback((device: Device | null) => {
    if (
      device?.DeviceLocation?.latitude &&
      device?.DeviceLocation?.longitude &&
      mapRef.current
    ) {
      const position = {
        lat: device.DeviceLocation.latitude,
        lng: device.DeviceLocation.longitude,
      };

      // Using setTimeout to ensure the operation happens after rendering
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.panTo(position);
          mapRef.current.setZoom(15); // Zoom in when a device is selected
        }
      }, 100);
    }
  }, []);

  // Update internal state when external state changes
  useEffect(() => {
    if (externalSelectedDevice) {
      setInternalSelectedDevice(null);
      // Hide button when programmatically centering on a device
      if (buttonRef.current) {
        buttonRef.current.classList.remove("visible");
      }
      centerMapOnDevice(externalSelectedDevice);
    }
  }, [externalSelectedDevice, centerMapOnDevice]);

  // Calculate center based on selected device or all devices
  const center = useMemo(() => {
    // If there's a selected device with coordinates, use it as center
    if (
      selectedDevice?.DeviceLocation?.latitude &&
      selectedDevice?.DeviceLocation?.longitude
    ) {
      return {
        lat: selectedDevice.DeviceLocation.latitude,
        lng: selectedDevice.DeviceLocation.longitude,
      };
    }

    // Otherwise use the average of all devices with coordinates
    const devicesWithCoordinates = devices.filter(
      (device) =>
        device.DeviceLocation &&
        device.DeviceLocation.latitude &&
        device.DeviceLocation.longitude,
    );

    // If no devices have valid coordinates, use a default center
    if (devicesWithCoordinates.length === 0) {
      return { lat: 0, lng: 0 };
    }

    const total = devicesWithCoordinates.reduce(
      (acc, device) => {
        return {
          lat: acc.lat + device.DeviceLocation.latitude,
          lng: acc.lng + device.DeviceLocation.longitude,
        };
      },
      { lat: 0, lng: 0 },
    );

    return {
      lat: total.lat / devicesWithCoordinates.length,
      lng: total.lng / devicesWithCoordinates.length,
    };
  }, [devices, selectedDevice]);

  // Effect to center the map when the selected device changes
  useEffect(() => {
    if (selectedDevice) {
      // Hide button when programmatically centering on a device
      if (buttonRef.current) {
        buttonRef.current.classList.remove("visible");
      }
    }
    centerMapOnDevice(selectedDevice);
  }, [selectedDevice, centerMapOnDevice]);

  const handleDeviceSelect = (device: Device) => {
    setInternalSelectedDevice(device);
    // Hide button when programmatically selecting a device
    if (buttonRef.current) {
      buttonRef.current.classList.remove("visible");
    }
    if (onDeviceSelect) {
      onDeviceSelect(device);
    }
    centerMapOnDevice(device);
  };

  const markers: Markers[] = devices
    .filter(
      (device) =>
        device.DeviceLocation &&
        device.DeviceLocation.latitude &&
        device.DeviceLocation.longitude &&
        (isAdmin ? true : device.linkStatus === "accepted"), // Only include devices with "accepted" status
    )
    .map((device) => {
      // Check if device has alarms
      const shadowData = device.shadowData || {};
      const alarmValue = shadowData.alarms ?? 0;

      // Determine alarm type based on the specified thresholds
      const hasError = alarmValue > 255;
      const hasWarning = alarmValue >= 1 && alarmValue <= 255;

      // Determine marker color based on device state - using Google Maps default marker colors
      let markerColor = "red"; // Default red color (Google Maps default)

      if (hasError) {
        // Keep red color for devices with errors (default Google Maps color)
        markerColor = "red";
      } else if (hasWarning) {
        // Orange/yellow color for devices with warnings
        markerColor = "orange";
      } else {
        // Green color for devices without alarms
        markerColor = "green";
      }

      console.log(
        `Device ${device.serialNumber}: alarmValue=${alarmValue}, hasError=${hasError}, hasWarning=${hasWarning}, markerColor=${markerColor}`,
      );

      // Create consistent Google Maps markers with colors - using the same format for all colors
      let markerIcon = undefined;

      if (typeof google !== "undefined" && google.maps) {
        try {
          markerIcon = {
            url: `https://maps.google.com/mapfiles/ms/icons/${markerColor}-dot.png`,
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 32),
          };
        } catch (error) {
          console.error("Error creating colored marker:", error);
          // Fallback to default red marker URL if there's an error
          markerIcon = {
            url: `https://maps.google.com/mapfiles/ms/icons/red-dot.png`,
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 32),
          };
        }
      }

      return {
        label: device.serialNumber,
        lat: device.DeviceLocation.latitude,
        lng: device.DeviceLocation.longitude,
        onClick: () => {
          handleDeviceSelect(device);
        },
        icon: markerIcon, // undefined = default red marker, defined = colored marker
      };
    });

  // Helper function to get alarm status label and badge
  const getAlarmBadge = (device: Device) => {
    const alarmValue = device.shadowData?.alarms ?? 0;

    if (alarmValue > 255) {
      return <ErrorBadge title="This device has errors">Error</ErrorBadge>;
    } else if (alarmValue >= 1 && alarmValue <= 255) {
      return (
        <WarningBadge title="This device has warnings">Warning</WarningBadge>
      );
    }

    return null;
  };

  return (
    <MapContainer>
      <GoogleMaps
        address={selectedDevice?.DeviceLocation?.address || ""}
        latitude={center.lat}
        longitude={center.lng}
        draggable={true}
        markers={markers}
        zoom={selectedDevice ? 15 : 12}
        fitBounds={!selectedDevice}
        onMapLoad={(map) => {
          mapRef.current = map;

          // Add a small delay before adding event listeners to avoid triggering on initial load
          setTimeout(() => {
            // Add event listeners for map movement (only after initial load)
            map.addListener("dragend", () => {
              if (buttonRef.current) {
                buttonRef.current.classList.add("visible");
              }
            });
            map.addListener("zoom_changed", () => {
              if (buttonRef.current) {
                buttonRef.current.classList.add("visible");
              }
            });
          }, 1000);

          // If there is already a selected device, center the map on it
          if (selectedDevice) {
            centerMapOnDevice(selectedDevice);
          }
        }}
      />
      {selectedDevice &&
        selectedDevice.DeviceLocation &&
        selectedDevice.DeviceLocation.latitude &&
        selectedDevice.DeviceLocation.longitude && (
          <InfoWindow
            position={{
              lat: selectedDevice.DeviceLocation.latitude,
              lng: selectedDevice.DeviceLocation.longitude,
            }}
            onCloseClick={() => {
              setInternalSelectedDevice(null);
              if (onDeviceSelect) {
                onDeviceSelect(null);
              }
            }}
          >
            <InfoWindowContent>
              <DeviceName>
                {selectedDevice.serialNumber}
                {getAlarmBadge(selectedDevice)}
              </DeviceName>
              <DeviceInfo>Status: {selectedDevice.linkStatus}</DeviceInfo>
              <DeviceInfo>
                Firmware: {selectedDevice.firmwareVersion || "N/A"}
              </DeviceInfo>
              {selectedDevice.DeviceLocation.address && (
                <DeviceInfo>
                  Location: {selectedDevice.DeviceLocation.address}
                </DeviceInfo>
              )}
              <ViewDetailsLink
                to={buildDeviceDetailsPath({
                  serialNumber: selectedDevice.serialNumber,
                  linkId: selectedDevice.linkId,
                  isAdminView: isAdmin,
                })}
              >
                View Details
              </ViewDetailsLink>
            </InfoWindowContent>
          </InfoWindow>
        )}
      {/* <SearchByLocationButton ref={buttonRef}>
        <Button
          label="Search by this location"
          icon="pi pi-search"
          onClick={() => {
            if (mapRef.current) {
              const center = mapRef.current.getCenter();
              if (center) {
                onLocationSearch?.(center.lat(), center.lng(), 5); // Default radius 5km
              }
            }
          }}
          style={{
            width: "200px",
            backgroundColor: theme.colors.tertiary,
            borderColor: theme.colors.tertiary,
            color: "white",
          }}
          className="p-button-outlined"
        />
      </SearchByLocationButton> */}
      {/* {isLocationSearchActive && (
        <LocationSearchActiveIndicator>
          <i
            className="pi pi-map-marker"
            style={{ fontSize: "1.5rem", color: "#007ad9" }}
          />
          <span style={{ fontSize: "0.9rem", color: "#333" }}>
            Location search active
          </span>
          <Button
            icon="pi pi-times"
            onClick={onClearLocationSearch}
            style={{ width: "30px", height: "30px", padding: "0" }}
            className="p-button-rounded p-button-outlined"
          />
        </LocationSearchActiveIndicator>
      )} */}
    </MapContainer>
  );
};

export default memo(DevicesMap);
